/**
 * Inspector chrome geometry - scroll-driven brand-bar collapse plus the
 * :root CSS variables descendants read for sticky offsets and toast
 * positioning.
 *
 * Chrome collapse on scroll: once the user has scrolled past the
 * breadcrumb + page-header strip (~200px), the teal brand bar slides out
 * of view and the nav row alone stays sticky - reclaiming ~64px of
 * vertical space for long tables. Below ~140px the brand bar is restored
 * (hysteresis avoids flicker around the threshold). The current chrome
 * height is published via `--app-chrome-height` on
 * `document.documentElement` so DataViewTable's sticky offsets
 * (which reference the variable through `CHROME_OFFSET_CSS`) track the
 * collapse without React re-renders.
 *
 * Extracted from `InspectorLayout.tsx` as a pure structural move - the
 * three effects are byte-identical to the inline originals.
 */
import { useEffect, useState } from 'react'
import {
  CHROME_OFFSET,
  CHROME_OFFSET_COLLAPSED,
} from '../components/shared/DataViewTable/stickyOffsets'

export function useAppChromeCollapse() {
  const [chromeCollapsed, setChromeCollapsed] = useState(false)

  // Inspector has no left sidebar, so the content region starts at the
  // viewport edge: publish a 0 content-left offset on :root for the global
  // toast provider (admin publishes its live drawer width). The toast's own
  // fallback is 0px, so this is belt-and-suspenders against layout-swap timing.
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--app-content-left', '0px')
    return () => {
      root.style.removeProperty('--app-content-left')
    }
  }, [])

  useEffect(() => {
    const COLLAPSE_AT = 200
    const EXPAND_AT = 140
    let ticking = false
    const apply = () => {
      ticking = false
      const y = window.scrollY
      setChromeCollapsed((prev) => {
        if (!prev && y > COLLAPSE_AT) return true
        if (prev && y < EXPAND_AT) return false
        return prev
      })
    }
    const onScroll = () => {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(apply)
    }
    apply()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const value = `${chromeCollapsed ? CHROME_OFFSET_COLLAPSED : CHROME_OFFSET}px`
    document.documentElement.style.setProperty('--app-chrome-height', value)
    return () => {
      document.documentElement.style.removeProperty('--app-chrome-height')
    }
  }, [chromeCollapsed])

  return { chromeCollapsed }
}
